import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { PageHeading, LockAvatar } from "./recoverPassword/components";
import { InputV2 } from "./subComponents/input";
import { ButtonV2 } from "./subComponents/button";
import { POPUP_TYPE } from './subComponents/commonModal';
import { CommonPortalContext } from "../contexts/commonPortal";
import { IPopup } from '../constants/commonPortal.constant';
import { VISITS_URL_V2 } from '../constants/patientChart.constant';
import Logo from "../assets/logo.png";

const allowedCharactersInPassword = /[^a-zA-Z0-9!@#$%^*-]/gm;

export const Login = ({
  history = {
    push: () => {},
  },
  error = false,

  Validate: validate = async (username = '', password = '') => {},
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  /**
   * @type {[
   *  Map<string, {
   *    error: boolean;
   *    helperText: string;
   *  }>,
   *  (error: Map<string, {
   *    error: boolean;
   *    helperText: string;
   *  }>) => void
   * ]}
   */
  const [inputError, setInputError] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const { handler: { handleSnackbarClose, handleSnackbarOpen } } = useContext(CommonPortalContext);

  const disabled = useMemo(() => {
    if (inputError.size > 0) {
      return true;
    }

    if (username.trim().length < 3) {
      return true;
    }

    if (password.trim().length < 8) {
      return true;
    }

    return false;
  }, [username, password, inputError]);

  const handleChange = (e) => {
    const __iE = new Map();
    const { name, value } = e.target;

    switch (name) {
      case "username": {
        setUsername(value);
        break;
      }
      case "password": {
        setPassword(value);
        if (allowedCharactersInPassword.test(value)) {
          __iE.set("passwordError", {
            error: true,
            helperText: "Only !@#$%^*- characters are allowed in password.",
          });
        }
        setInputError(__iE);
        break;
      }
      default: {
        break;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // show loading button
      setLoading(true);

      const { data, status } = await validate(username, password);
      if (status === 200) {
        setLoading(false);

        handleSnackbarOpen(IPopup({
          open: true,
          title: 'Successful',
          type: POPUP_TYPE.SUCCESS,
          message: 'Signed in successfully!',
          onOk: handleSnackbarClose,
        }));

        const {
          Data = {
            UserID: 0,
            CompanyID: 0,
          },
        } = data;

        localStorage.setItem('userData', JSON.stringify(Data));
        localStorage.setItem('userId', Data.UserID);
        localStorage.setItem('ppLastSavedTime', new Date());

        history.push(VISITS_URL_V2);
      }

      if (status !== 200) {
        const {
          Error: message = 'Failed to sign in. Please try later.',
          Status: title = 'Error',
        } = data;

        handleSnackbarOpen(IPopup({
          open: true,
          title,
          type: POPUP_TYPE.ERROR,
          message,
          onOk: handleSnackbarClose,
        }));
      }

      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleSnackbarOpen(IPopup({
        open: true,
        title: 'Error',
        type: POPUP_TYPE.ERROR,
        message: 'Failed to sign in. Please try later.',
        onOk: handleSnackbarClose,
      }));
    }
  };

  const handleClickShowPassword = useCallback((e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  }, [showPassword]);

  const handleMouseDownPassword = useCallback((e) => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (localStorage.getItem("userData") && localStorage.getItem("userId")) {
      history.push(VISITS_URL_V2);
    }
  }, []);

  /**
   * @type {{
   *  error: boolean;
   *  helperText: string;
   * }}
   */
  const passwordHelper = useMemo(() => {
    if (!inputError.has("passwordError")) {
      return {
        error: false,
        helperText: "",
      };
    }

    return inputError.get("passwordError");
  }, [inputError]);

  return (
    <div
      className="sign-in-container flex flex-column flex-fill"
      data-app="app-component-wrapper"
      data-view="sign-in"
    >
      <PageHeading logo={Logo} />

      <div className="sign-in-content flex flex-fill align-items-center justify-content-center p-l-6 p-r-6">
        <div className="flex flex-column gap-16 width-360 max-width-360 min-height-520">
          <div className="flex align-items-center justify-content-center">
            <LockAvatar />
          </div>

          <div className="flex align-items-center justify-content-center font-24">
            Sign In
          </div>

          <form className="flex flex-column gap-16" onSubmit={handleSubmit}>
            {error && (
              <Typography component="p" variant="p" color="error">
                The username or password provided is incorrect. Please try again or click 'Forgot Password'.
              </Typography>
            )}

            <InputV2
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="Username"
              value={username}
              autoFocus
              onChange={handleChange}
            />

            <InputV2
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              autoComplete="current-password"
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      size='small'
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <Visibility fontSize='small' /> : <VisibilityOff fontSize='small' />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={passwordHelper.error}
              message={passwordHelper.helperText}
            />

            <div className="flex justify-content-flex-end">
              <Link to="/recover-password" className="color-primary-v2 font-14">
                Forgot Password?
              </Link>
            </div>

            <ButtonV2
              type="submit"
              label="Sign In"
              id="SigninButton"
              loading={loading}
              disabled={disabled}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
